import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Button,
  Dialog,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import { getStandardDialogPaperSx, standardDialogBackdropSx } from "./StandardDialog";
import {
  galleryInquiryBodySx,
  galleryInquiryCaptionSx,
  galleryInquiryHeaderInsetSx,
  galleryInquiryLegalLinkSx,
  galleryInquirySuccessIconWellSx,
  galleryInquiryTitleSx,
  getGalleryInquiryCloseButtonSx,
  getGalleryInquiryDialogSx,
  getGalleryInquiryEmailFieldSx,
  getGalleryInquiryPaperSx,
  getGalleryInquiryPrimaryFullWidthCtaSx,
} from "./galleryInquiryModalStyles";
import { normalizeEmail } from "../utils/sessionAudience";
import { MF_PRIVACY_URL, MF_TERMS_URL } from "../utils/legalLinks";
import { ReactComponent as UploadCompleteIcon } from "../Assets/upload-complete.svg";

const INQUIRY_DIALOG_TITLE_ID = "gallery-inquire-modal-title";

/**
 * Says only what is true at this moment. The send to the property team happens
 * after the response returns and can still find no configured recipient, so the
 * browser has no delivery outcome to report here. The confirmation email carries
 * the specific outcome moments later.
 */
const INQUIRY_CONFIRMATION_TITLE = "We received your interest";

function emailErrorMessage(value) {
  const trimmed = typeof value === "string" ? value.trim() : "";
  if (!trimmed) return "Enter your email";
  if (!normalizeEmail(trimmed)) return "Enter a valid email address";
  return "";
}

/**
 * `coversLaterTaps` is load-bearing rather than cosmetic: it is what the viewer
 * agrees to when a single confirmation stands in for every later tap. Scoped to
 * "this residence", it would authorise the tap in front of them and nothing
 * after it.
 */
function InquiryConsentText({ coversLaterTaps = false }) {
  return (
    <Typography sx={{ ...galleryInquiryCaptionSx, display: "block", mt: 1.5 }}>
      By tapping I&apos;m Interested, you agree that MagicFurnish and the property
      team may email you about your interest in this residence.{" "}
      {coversLaterTaps
        ? "On other residences, tapping I'm Interested sends your email the same way, without this step. "
        : ""}
      See our{" "}
      <Box
        component="a"
        href={MF_TERMS_URL}
        target="_blank"
        rel="noopener noreferrer"
        sx={galleryInquiryLegalLinkSx}
      >
        Terms
      </Box>{" "}
      and{" "}
      <Box
        component="a"
        href={MF_PRIVACY_URL}
        target="_blank"
        rel="noopener noreferrer"
        sx={galleryInquiryLegalLinkSx}
      >
        Privacy
      </Box>
      .
    </Typography>
  );
}

export default function GalleryInquireModal({
  open,
  onClose,
  isGuest,
  accountEmail,
  onSubmit,
  submitting,
  confirmState,
  error,
  inquiriesAvailable = true,
  guestEmailPrefill = "",
  knownEmail = "",
}) {
  const theme = useTheme();
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  /** Escape hatch from the confirmation for a viewer whose account email is stale. */
  const [useDifferentEmail, setUseDifferentEmail] = useState(false);
  const primaryFullWidthCtaSx = getGalleryInquiryPrimaryFullWidthCtaSx(theme);
  const emailFieldSx = getGalleryInquiryEmailFieldSx(theme);
  const closeButtonSx = getGalleryInquiryCloseButtonSx(theme);
  /** Autofocus would open the soft keyboard over the consent copy and CTA. */
  const focusEmailOnOpen = useMediaQuery(theme.breakpoints.up("sm"));

  useEffect(() => {
    if (!open) return;
    if (confirmState) return;
    setEmail(isGuest ? guestEmailPrefill || "" : accountEmail || "");
    setEmailError("");
    setUseDifferentEmail(false);
  }, [open, isGuest, accountEmail, confirmState, guestEmailPrefill]);

  /**
   * Viewers reach the form with no address we can send to — either we hold none,
   * or they asked to use another — so the typed value is always what is sent.
   */
  const handleSubmit = async (event) => {
    event?.preventDefault?.();
    const validationError = emailErrorMessage(email);
    if (validationError) {
      setEmailError(validationError);
      return;
    }
    setEmailError("");
    await onSubmit(email);
  };

  const handleConfirmSubmit = async (event) => {
    event?.preventDefault?.();
    await onSubmit(knownEmail);
  };

  const confirmed = Boolean(confirmState);
  /**
   * Nothing to fill in — we hold the address and only need the viewer to agree
   * to it being shared. Reaching this at all means they have not agreed before,
   * since afterwards the CTA sends without opening anything.
   */
  const showKnownEmailConfirm = Boolean(knownEmail) && !useDifferentEmail;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      aria-labelledby={INQUIRY_DIALOG_TITLE_ID}
      sx={getGalleryInquiryDialogSx()}
      BackdropProps={{ sx: standardDialogBackdropSx }}
      PaperProps={{
        elevation: 0,
        sx: [
          getStandardDialogPaperSx(theme),
          getGalleryInquiryPaperSx(),
          { position: "relative" },
        ],
      }}
    >
      <IconButton
        aria-label="Close"
        onClick={onClose}
        size="small"
        sx={closeButtonSx}
      >
        <CloseIcon sx={{ fontSize: 20 }} />
      </IconButton>
      {confirmed ? (
        <Box sx={{ textAlign: "center" }}>
          <Box sx={galleryInquirySuccessIconWellSx}>
            <UploadCompleteIcon style={{ width: 26, height: 26 }} />
          </Box>
          <Typography
            id={INQUIRY_DIALOG_TITLE_ID}
            variant="h6"
            sx={{ ...galleryInquiryTitleSx, mb: 2 }}
          >
            {INQUIRY_CONFIRMATION_TITLE}
          </Typography>
          <Button
            type="button"
            variant="contained"
            onClick={onClose}
            disableElevation
            fullWidth
            autoFocus
            sx={primaryFullWidthCtaSx}
          >
            Continue exploring
          </Button>
        </Box>
      ) : !inquiriesAvailable ? (
        <Box sx={{ textAlign: "center" }}>
          <Typography
            id={INQUIRY_DIALOG_TITLE_ID}
            variant="h6"
            sx={{ ...galleryInquiryTitleSx, mb: 1 }}
          >
            Not available yet
          </Typography>
          <Typography sx={{ ...galleryInquiryBodySx, mb: 2 }}>
            This floor plan isn&apos;t set up to receive interest yet. Check back later
            or ask the person who shared the link with you.
          </Typography>
          <Button
            variant="contained"
            onClick={onClose}
            disableElevation
            fullWidth
            sx={primaryFullWidthCtaSx}
          >
            OK
          </Button>
        </Box>
      ) : showKnownEmailConfirm ? (
        <Box component="form" onSubmit={handleConfirmSubmit} noValidate>
          <Typography
            id={INQUIRY_DIALOG_TITLE_ID}
            variant="h6"
            sx={{ ...galleryInquiryTitleSx, ...galleryInquiryHeaderInsetSx, mb: 0.5 }}
          >
            Interested in this residence?
          </Typography>
          <Typography sx={{ ...galleryInquiryBodySx, mb: 2 }}>
            We&apos;ll share <strong>{knownEmail}</strong> with the property team so
            they can get in touch.
          </Typography>
          {error ? (
            <Typography
              role="alert"
              variant="body2"
              sx={{ color: theme.palette.error.main, mb: 1.5 }}
            >
              {error}
            </Typography>
          ) : null}
          <Button
            type="submit"
            variant="contained"
            disabled={submitting}
            disableElevation
            fullWidth
            autoFocus
            sx={primaryFullWidthCtaSx}
          >
            {submitting ? "Sending…" : "I'm Interested"}
          </Button>
          <Button
            type="button"
            variant="text"
            onClick={() => {
              setEmail("");
              setEmailError("");
              setUseDifferentEmail(true);
            }}
            fullWidth
            sx={{ mt: 1, textTransform: "none" }}
          >
            Use a different email
          </Button>
          <InquiryConsentText coversLaterTaps />
        </Box>
      ) : (
        <Box component="form" onSubmit={handleSubmit} noValidate>
          <Typography
            id={INQUIRY_DIALOG_TITLE_ID}
            variant="h6"
            sx={{ ...galleryInquiryTitleSx, ...galleryInquiryHeaderInsetSx, mb: 0.5 }}
          >
            Interested in this residence?
          </Typography>
          <Typography sx={{ ...galleryInquiryBodySx, mb: 2 }}>
            Enter your email so the property team can get in touch.
          </Typography>
          <TextField
            fullWidth
            label="Email"
            placeholder="you@email.com"
            type="email"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              if (emailError) setEmailError(emailErrorMessage(e.target.value));
            }}
            onBlur={() => {
              if (email.trim()) setEmailError(emailErrorMessage(email));
            }}
            autoFocus={focusEmailOnOpen}
            autoComplete="email"
            error={Boolean(emailError)}
            helperText={emailError || undefined}
            InputLabelProps={{ shrink: true }}
            InputProps={{ style: { height: 48 } }}
            inputProps={{
              inputMode: "email",
              autoCapitalize: "none",
              autoCorrect: "off",
              spellCheck: false,
              enterKeyHint: "send",
            }}
            sx={{ mb: 1.5, ...emailFieldSx }}
          />
          {error ? (
            <Typography
              role="alert"
              variant="body2"
              sx={{ color: theme.palette.error.main, mb: 1.5 }}
            >
              {error}
            </Typography>
          ) : null}
          <Button
            type="submit"
            variant="contained"
            disabled={submitting || !normalizeEmail(email)}
            disableElevation
            fullWidth
            sx={primaryFullWidthCtaSx}
          >
            {submitting ? "Sending…" : "I'm Interested"}
          </Button>
          <InquiryConsentText />
        </Box>
      )}
    </Dialog>
  );
}

GalleryInquireModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  isGuest: PropTypes.bool.isRequired,
  accountEmail: PropTypes.string,
  onSubmit: PropTypes.func.isRequired,
  submitting: PropTypes.bool,
  confirmState: PropTypes.shape({
    email: PropTypes.string,
    kind: PropTypes.oneOf(["submitted", "new_notified", "duplicate"]),
  }),
  error: PropTypes.string,
  inquiriesAvailable: PropTypes.bool,
  guestEmailPrefill: PropTypes.string,
  knownEmail: PropTypes.string,
};

InquiryConsentText.propTypes = {
  coversLaterTaps: PropTypes.bool,
};
